import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService, DistributionGroupService, GmAssignmentService, UserService } from '@org/shared-services';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Task } from '@org/interfaces';

@Component({
    selector: 'lib-assign-to-dropdown',
    imports: [CommonModule, FormsModule],
    templateUrl: './assignToDropdown.component.html',
    styleUrl: './assignToDropdown.component.scss'
})
export class AssignToDropdownComponent implements OnInit {
  @Input() task!: Task; 
  searchTerm: string = '';
  selectedOptions: string[] = [];
  filteredGlobalGroups: any[] = [];
  filteredPrivateGroups: any[] = [];
  filteredUsers: any[] = [];
  tenantUsers: any[] = [];
  globalGroups: any[] = [];
  privateGroups: any[] = [];
  private searchSubject = new Subject<string>();
  dropdownAssignedToOpen = false; // Flag to control dropdown state
  selectedOption: string = ''; // Holds the selected option text
  user: any = null;
  currentTenant: any = null;

  constructor(
    private distroGroupService: DistributionGroupService,
    private userService: UserService,
    private authService: AuthService,
    private gmAssignmentService: GmAssignmentService
  ) {}

  ngOnInit() {
    this.user = this.authService.currentUserValue;
    if (this.user && this.user.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.initAfterTenant(this.currentTenant);
      });
    } else {
      this.currentTenant = this.user?.tenant;
      this.initAfterTenant(this.currentTenant);
    }
  }

  initAfterTenant(tenantContext: string) {
    this.distroGroupService.getDistributionGroupsByTenant(tenantContext).subscribe(groups => {
      this.globalGroups = groups.data.filter((group: { isGlobal: boolean }) => group.isGlobal);
      this.privateGroups = groups.data.filter((group: { isGlobal: boolean }) => !group.isGlobal);
      this.filterOptions();
    });

    this.userService.getUsersByTenant(tenantContext).subscribe(users => {
      this.tenantUsers = users;
      this.filterOptions();
    });

    // Handle search input with debounce
    this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
      this.filterOptions();
    });
  }

  // Toggle the dropdown and search visibility
  toggleDropdown() {
    this.dropdownAssignedToOpen = !this.dropdownAssignedToOpen;
  }

  // Filter options based on search term
  filterOptions() {
    const searchLower = this.searchTerm.toLowerCase();

    // Filter Global Groups
    this.filteredGlobalGroups = this.globalGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );

    // Filter Private Groups
    this.filteredPrivateGroups = this.privateGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );

    // Filter Users
    this.filteredUsers = this.tenantUsers.filter(user => 
      user.name.toLowerCase().includes(searchLower)
    );
  }

  // Trigger search on input change
  onSearchInput(event: any) {
    this.searchTerm = event.target.value;
    this.searchSubject.next(this.searchTerm);
  }

  // Update selected option text when user selects an option
  selectOption(option: string) {
    this.selectedOption = option;
    this.dropdownAssignedToOpen = false; // Close dropdown after selection
  }

  private getTenantContext(): string {
    if (this.user && this.user.roles && this.user.roles.includes('gm')) {
      return this.currentTenant;
    }
    return this.user?.tenant;
  }
}
